// Line-art pumpjack ("nodding donkey"). The walking beam rocks on the A-frame
// pivot to evoke "pumping data out of the statements". Brand green on a
// transparent background so it sits on the cream card. Respects the user's
// reduce-motion preference and goes still when `animate` is false.

interface PumpjackProps {
  animate?: boolean;
  size?: number;
  className?: string;
}

const DEEP = '#1B4332';
const MID = '#2D6A4F';
const SOFT = '#A9BBAE';

export default function Pumpjack({ animate = true, size = 96, className = '' }: PumpjackProps) {
  return (
    <div
      className={className}
      style={{ width: size, height: Math.round(size * 0.72) }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes shale-pj-rock {
          0%   { transform: rotate(8deg); }
          50%  { transform: rotate(-8deg); }
          100% { transform: rotate(8deg); }
        }
        .shale-pj-beam {
          transform-box: view-box;
          transform-origin: 108px 50px;
          animation: shale-pj-rock 3.2s ease-in-out infinite;
        }
        .shale-pj-still .shale-pj-beam {
          animation: none;
          transform: rotate(0deg);
        }
        @media (prefers-reduced-motion: reduce) {
          .shale-pj-beam { animation: none; transform: rotate(-5deg); }
        }
      `}</style>
      <svg
        viewBox="0 0 200 140"
        width="100%"
        height="100%"
        fill="none"
        className={animate ? '' : 'shale-pj-still'}
      >
        {/* Ground */}
        <line x1="6" y1="124" x2="194" y2="124" stroke={SOFT} strokeWidth="3" strokeLinecap="round" />

        {/* Well head (static) — the polished rod telescopes into this */}
        <rect x="33" y="110" width="16" height="14" rx="2" stroke={SOFT} strokeWidth="3" />

        {/* Base skid + motor (static) */}
        <line x1="118" y1="124" x2="182" y2="124" stroke={SOFT} strokeWidth="3" strokeLinecap="round" />
        <circle cx="158" cy="112" r="8" stroke={MID} strokeWidth="3" />

        {/* A-frame / Samson post (static) */}
        <line x1="108" y1="50" x2="88" y2="124" stroke={MID} strokeWidth="4" strokeLinecap="round" />
        <line x1="108" y1="50" x2="128" y2="124" stroke={MID} strokeWidth="4" strokeLinecap="round" />
        <line x1="96" y1="98" x2="120" y2="98" stroke={MID} strokeWidth="3" strokeLinecap="round" />

        {/* Rocking walking beam group (pivots on the apex) */}
        <g className="shale-pj-beam">
          {/* Walking beam */}
          <line x1="44" y1="50" x2="172" y2="50" stroke={DEEP} strokeWidth="7" strokeLinecap="round" />

          {/* Horse head (front) */}
          <path
            d="M 52 44 Q 36 45 35 53 Q 34 61 47 60 L 50 52 Z"
            fill={DEEP}
          />

          {/* Polished rod + bridle down into the well head */}
          <line x1="40" y1="58" x2="40" y2="114" stroke={DEEP} strokeWidth="3.5" strokeLinecap="round" />

          {/* Counterweight + crank (back) */}
          <line x1="160" y1="50" x2="168" y2="64" stroke={DEEP} strokeWidth="4" strokeLinecap="round" />
          <circle cx="171" cy="52" r="11" fill={DEEP} />

          {/* Pivot pin */}
          <circle cx="108" cy="50" r="4.5" fill={MID} />
        </g>
      </svg>
    </div>
  );
}
